import { useTranslation } from 'react-i18next';
import SEOMeta from '../../components/feature/SEOMeta';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import TrustBar from './components/TrustBar';
import AboutSection from './components/AboutSection';
import ServicesPreview from './components/ServicesPreview';
import WhyArmenia from './components/WhyArmenia';
import WhyUs from './components/WhyUs';
import HowWeWork from './components/HowWeWork';
import FAQSection from './components/FAQSection';
import FinalCTA from './components/FinalCTA';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const homeJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'ProfessionalService'],
    name: 'ArmeniaSettle',
    description:
      'Relocation and business support services for Chinese citizens in Armenia. Housing search, office search, business registration, legal and accounting support, and residence permit assistance.',
    url: siteUrl,
    email: 'haykagab@gmail.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Yerevan',
      addressCountry: 'AM',
    },
    areaServed: {
      '@type': 'Country',
      name: 'Armenia',
    },
    sameAs: ['https://t.me/haykagab'],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Relocation and Business Support Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Housing Search in Armenia' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Office Search for Business' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Business Registration in Armenia' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Legal Support' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Accounting Support' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Legalization and Residence Permit' } },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ArmeniaSettle',
    url: siteUrl,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Can I start working with you before I arrive in Armenia?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes — and most of our clients do. We handle the entire pre-arrival process remotely: initial consultation, company registration, housing shortlist, bank account preparation, and residence permit documents can all be started before you land. You arrive to a situation that is already largely prepared.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does the full relocation and business setup process take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Company registration in Armenia typically takes 3 to 7 business days. Finding and securing housing depends on your requirements and availability, but most clients are settled within 2 to 4 weeks. Residence permits vary by type but usually take several weeks to process. We give you a realistic timeline at the start and keep you updated throughout.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can you help with opening a bank account in Armenia?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. We guide you through the banking process, including which banks are most accessible for Chinese nationals, what documentation is required, and how to prepare. We accompany you to the appointment or provide clear preparation materials depending on your situation.',
        },
      },
      {
        '@type': 'Question',
        name: 'What documents do I need to prepare before we start?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'It depends on what you need — business registration, housing, or a residence permit each have different requirements. At the start of our engagement, we provide you with a clear, personalised document checklist so there are no surprises. We also help you prepare and certify documents as needed.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do you provide support in Chinese?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Our team communicates fluently in Chinese throughout the entire process — consultations, document explanations, status updates, and follow-up questions. We also work in Russian and English if preferred.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use several services at the same time?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, and it is usually the most efficient approach. Many clients register their company, look for an office, and begin the residence permit process in parallel. We coordinate the timelines so these workstreams do not conflict — and often accelerate each other.',
        },
      },
      {
        '@type': 'Question',
        name: 'What happens after the initial setup is complete?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We do not hand you off and disappear. Many clients continue working with us for ongoing accounting, legal support, permit renewals, and general questions about operating in Armenia. We are a long-term partner, not a one-time service.',
        },
      },
      {
        '@type': 'Question',
        name: 'I am still evaluating whether to move to Armenia. Can I contact you?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely — and this is one of the most common first steps. We can give you a realistic overview of what relocation and business setup actually involves in terms of costs, timelines, and requirements. There is no commitment and no pressure. Many of our long-term clients started with a single exploratory conversation.',
        },
      },
    ],
  },
];

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';

  return (
    <div className="min-h-screen bg-white">
      <SEOMeta
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        canonical={`${siteUrl}/`}
        lang={lang}
        jsonLd={homeJsonLd}
      />
      <Header />
      <main>
        <HeroSection />
        <TrustBar />
        <AboutSection />
        <ServicesPreview />
        <WhyArmenia />
        <WhyUs />
        <HowWeWork />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
